/**
 * ScenarioProjection Service
 *
 * Applies scenario deltas to base org chart state to create projected views.
 * Uses pre-computed status indexes for O(1) node status lookups during rendering.
 *
 * Key features:
 * - Delta-based projection (only processes changes, not full copies)
 * - Memoization scoped to immutable scene and delta identities
 * - Pre-computed status index for O(1) node status lookups
 * - Structural sharing for memory efficiency
 */
import type { Node, Edge } from '../../../core/models/scene';
import {
  type ScenarioDelta,
  type NodeAddition,
  type NodeId,
  type ScenarioStatusIndex,
  type NodeScenarioStatus,
  buildStatusIndex,
  isDeltaEmpty,
  createNodeId,
} from '../models/Scenario';
import { isOrgEdge } from '../types/layout.types';
import { computeFullTreeLayout } from './graphAnalysis';
import { applyOrgChartScenarioFieldUpdate } from './orgChartScenarioFieldSync';

// =============================================================================
// Types
// =============================================================================

export interface ScenarioProjectionResult {
  nodes: Node[];
  edges: Edge[];
  statusIndex: ScenarioStatusIndex;
  changeCount: number;
}

export interface ScenarioProjectionSettings {
  /** Show removed nodes as ghost elements */
  showRemovedNodes: boolean;
  /** Include edges for removed nodes */
  showRemovedEdges: boolean;
}

const DEFAULT_SETTINGS: ScenarioProjectionSettings = {
  showRemovedNodes: true,
  showRemovedEdges: false,
};

// =============================================================================
// Node Conversion
// =============================================================================

/** Convert a NodeAddition to a Node for rendering */
function additionToNode(addition: NodeAddition): Node {
  return {
    id: addition.id as string,
    type: addition.type,
    name: addition.name,
    x: addition.position?.x ?? 0,
    y: addition.position?.y ?? 0,
    w: 200,
    h: 80,
    attrs: {
      name: addition.name,
      title: addition.title,
      department: addition.department,
      ...addition.props,
    },
  };
}

const LEVEL_SPACING = 120;
const NODE_SPACING = 40;

/** Calculate position for a new node relative to its manager */
function calculateAdditionPosition(
  addition: NodeAddition,
  baseNodes: Node[],
  baseEdges: Edge[],
  alreadyPlaced: Map<string, { x: number; y: number }>,
  additionManagerIndex: Map<string, string | undefined>,
): { x: number; y: number } | null {
  if (addition.position) return null;
  const managerId = addition.managerId as string | undefined;
  if (!managerId) return null;

  const manager = baseNodes.find((n) => n.id === managerId);
  if (!manager) {
    const placedPos = alreadyPlaced.get(managerId);
    if (!placedPos) return null;
    return { x: placedPos.x, y: placedPos.y + LEVEL_SPACING };
  }

  const managerX = manager.x ?? 0;
  const managerY = manager.y ?? 0;
  const managerW = (manager as unknown as Record<string, unknown>).width as number ?? (manager as unknown as Record<string, unknown>).w as number ?? 200;

  const siblingIds: string[] = [];
  for (const edge of baseEdges) {
    const edgeType = edge.type as string;
    if ((edgeType === 'reporting' || edgeType === 'reports_to') && edge.to === managerId && edge.from !== (addition.id as string)) {
      siblingIds.push(edge.from);
    }
  }

  const alreadyPlacedSiblings: Array<{ x: number; w: number }> = [];
  alreadyPlaced.forEach((pos, id) => {
    if (id === (addition.id as string)) return;
    if (additionManagerIndex.get(id) !== managerId) return;
    alreadyPlacedSiblings.push({ x: pos.x, w: 200 });
  });

  if (siblingIds.length === 0 && alreadyPlacedSiblings.length === 0) {
    return { x: managerX + managerW / 2 - 100, y: managerY + LEVEL_SPACING };
  }

  let rightEdge = -Infinity;
  let siblingY = managerY + LEVEL_SPACING;
  for (const sibId of siblingIds) {
    const sib = baseNodes.find((n) => n.id === sibId);
    if (sib) {
      const sibX = sib.x ?? 0;
      const sibW = (sib as unknown as Record<string, unknown>).width as number ?? (sib as unknown as Record<string, unknown>).w as number ?? 200;
      rightEdge = Math.max(rightEdge, sibX + sibW);
      if (sib.y != null) siblingY = sib.y;
    }
  }
  for (const placed of alreadyPlacedSiblings) {
    rightEdge = Math.max(rightEdge, placed.x + placed.w);
  }

  if (rightEdge === -Infinity) {
    return { x: managerX + managerW / 2 - 100, y: managerY + LEVEL_SPACING };
  }

  return { x: rightEdge + NODE_SPACING, y: siblingY };
}

/** Create a ghost node for removed items */
function createGhostNode(
  nodeId: NodeId,
  originalData: { name: string; title?: string; type: string }
): Node {
  return {
    id: nodeId as string,
    type: originalData.type,
    name: originalData.name,
    x: 0,
    y: 0,
    w: 200,
    h: 80,
    attrs: {
      name: originalData.name,
      title: originalData.title,
      _scenarioRemoved: true, // Marker for removed node
    },
  };
}

// =============================================================================
// Edge Computation
// =============================================================================

/** Compute edges after applying delta (handles moves) */
function computeEdgesWithDelta(
  baseEdges: Edge[],
  delta: ScenarioDelta,
  effectiveNodeIds: Set<string>,
  settings: ScenarioProjectionSettings
): Edge[] {
  const removedNodeIds = new Set(delta.removals.map((r) => r.nodeId as string));

  // Build a map of node moves for edge adjustment
  const moveMap = new Map<string, string | null>();
  for (const move of delta.moves) {
    moveMap.set(move.nodeId as string, move.newManagerId as string | null);
  }

  // Filter and adjust edges
  const edges: Edge[] = [];

  for (const edge of baseEdges) {
    const fromRemoved = removedNodeIds.has(edge.from);
    const toRemoved = removedNodeIds.has(edge.to);

    // Skip edges involving removed nodes (unless showing removed edges)
    if ((fromRemoved || toRemoved) && !settings.showRemovedEdges) {
      continue;
    }

    // Skip edges where nodes don't exist in effective set
    if (!effectiveNodeIds.has(edge.from) || !effectiveNodeIds.has(edge.to)) {
      continue;
    }

    // Apply move transformations for org edges
    if (isOrgEdge(edge) || edge.type === 'reports_to') {
      const newToManager = moveMap.get(edge.from);
      if (newToManager !== undefined) {
        // This node was moved - create new edge to new manager
        if (newToManager !== null && effectiveNodeIds.has(newToManager)) {
          edges.push({
            ...edge,
            id: `${edge.id}_scenario_moved`,
            to: newToManager,
          });
        }
        continue; // Skip original edge
      }
    }

    edges.push(edge);
  }

  // Add edges for newly added nodes (connecting to their managers)
  for (const addition of delta.additions) {
    if (addition.managerId && effectiveNodeIds.has(addition.managerId as string)) {
      edges.push({
        id: `scenario_edge_${addition.id}`,
        type: 'reporting',
        from: addition.id as string,
        to: addition.managerId as string,
        fromAnchor: 'top',
        toAnchor: 'bottom',
        attrs: {
          routingType: 'rounded',
          endArrow: { type: 'triangle', size: 8 },
        },
      });
    }
  }

  return edges;
}

// =============================================================================
// Main Projection Class
// =============================================================================

export class ScenarioProjection {
  // Memoization cache
  private static prevNodesRef: Node[] | null = null;
  private static prevEdgesRef: Edge[] | null = null;
  private static prevDelta: ScenarioDelta | null = null;
  private static prevSettings: ScenarioProjectionSettings | null = null;
  private static prevResult: ScenarioProjectionResult | null = null;

  /**
   * Apply a scenario delta to base nodes and edges.
   * Returns projected view with status index for O(1) lookups.
   */
  static applyDelta(
    baseNodes: Node[],
    baseEdges: Edge[],
    delta: ScenarioDelta,
    settings: Partial<ScenarioProjectionSettings> = {}
  ): ScenarioProjectionResult {
    const fullSettings = { ...DEFAULT_SETTINGS, ...settings };

    // Quick reference equality check
    if (
      this.prevResult &&
      this.prevNodesRef === baseNodes &&
      this.prevEdgesRef === baseEdges &&
      this.prevDelta === delta &&
      this.prevSettings?.showRemovedNodes === fullSettings.showRemovedNodes &&
      this.prevSettings?.showRemovedEdges === fullSettings.showRemovedEdges
    ) {
      return this.prevResult;
    }

    // Early exit for empty delta
    if (isDeltaEmpty(delta)) {
      const result: ScenarioProjectionResult = {
        nodes: baseNodes,
        edges: baseEdges,
        statusIndex: buildStatusIndex(delta),
        changeCount: 0,
      };
      this.cacheResult(baseNodes, baseEdges, delta, fullSettings, result);
      return result;
    }

    // Build indexes for O(1) lookups during processing
    const removalIds = new Set(delta.removals.map((r) => r.nodeId as string));
    const updateIndex = new Map<string, ScenarioDelta['updates']>();
    for (const update of delta.updates) {
      const updates = updateIndex.get(update.nodeId) ?? [];
      updates.push(update);
      updateIndex.set(update.nodeId, updates);
    }
    // Note: moveIndex is used in computeEdgesWithDelta for edge transformations
    // const moveIndex = new Map(delta.moves.map((m) => [m.nodeId as string, m]));

    // Process nodes
    const processedNodes: Node[] = [];
    const effectiveNodeIds = new Set<string>();

    // Filter out removed nodes, apply updates
    for (const node of baseNodes) {
      if (removalIds.has(node.id)) {
        // Add ghost node if showing removed
        if (fullSettings.showRemovedNodes) {
          const removal = delta.removals.find((r) => r.nodeId === node.id);
          if (removal) {
            const ghostNode = createGhostNode(
              createNodeId(node.id),
              removal.originalData
            );
            processedNodes.push(ghostNode);
            effectiveNodeIds.add(node.id);
          }
        }
        continue;
      }

      // Apply updates
      const updates = updateIndex.get(node.id);
      if (updates) {
        const updatedNode = updates.reduce<Node>((current, update) => applyOrgChartScenarioFieldUpdate(
          { ...current }, update.change.field, update.change.newValue,
        ), node);
        processedNodes.push(updatedNode);
      } else {
        processedNodes.push(node);
      }

      effectiveNodeIds.add(node.id);
    }

    const alreadyPlaced = new Map<string, { x: number; y: number }>();
    const additionManagerIndex = new Map<string, string | undefined>(
      delta.additions.map((a) => [a.id as string, a.managerId as string | undefined])
    );
    for (const addition of delta.additions) {
      const node = additionToNode(addition);
      if (!addition.position && addition.managerId) {
        const pos = calculateAdditionPosition(addition, baseNodes, baseEdges, alreadyPlaced, additionManagerIndex);
        if (pos) {
          node.x = pos.x;
          node.y = pos.y;
          alreadyPlaced.set(node.id, pos);
        }
      }
      processedNodes.push(node);
      effectiveNodeIds.add(addition.id as string);
    }

    const processedEdges = computeEdgesWithDelta(
      baseEdges,
      delta,
      effectiveNodeIds,
      fullSettings
    );

    const hasStructuralChanges = delta.additions.length > 0 || delta.removals.length > 0 || delta.moves.length > 0;
    let finalNodes = processedNodes;
    if (hasStructuralChanges) {
      const layoutPositions = computeFullTreeLayout(processedNodes, processedEdges);
      if (layoutPositions.size > 0) {
        finalNodes = processedNodes.map((n) => {
          const pos = layoutPositions.get(n.id);
          return pos ? { ...n, x: pos.x, y: pos.y } : n;
        });
      }
    }

    const statusIndex = buildStatusIndex(delta);
    const changeCount =
      delta.additions.length +
      delta.removals.length +
      delta.moves.length +
      delta.updates.length;

    const result: ScenarioProjectionResult = {
      nodes: finalNodes,
      edges: processedEdges,
      statusIndex,
      changeCount,
    };

    this.cacheResult(baseNodes, baseEdges, delta, fullSettings, result);
    return result;
  }

  /** Cache result for memoization */
  private static cacheResult(
    nodes: Node[],
    edges: Edge[],
    delta: ScenarioDelta,
    settings: ScenarioProjectionSettings,
    result: ScenarioProjectionResult
  ): void {
    this.prevNodesRef = nodes;
    this.prevEdgesRef = edges;
    this.prevDelta = delta;
    this.prevSettings = settings;
    this.prevResult = result;
  }

  /** Clear the cache (useful for testing or forced recomputation) */
  static clearCache(): void {
    this.prevNodesRef = null;
    this.prevEdgesRef = null;
    this.prevDelta = null;
    this.prevSettings = null;
    this.prevResult = null;
  }

  /**
   * Get the scenario status for a specific node.
   * Uses the pre-computed status index for O(1) lookup.
   */
  static getNodeStatus(
    nodeId: string,
    statusIndex: ScenarioStatusIndex | null
  ): NodeScenarioStatus {
    if (!statusIndex) return 'unchanged';

    const id = createNodeId(nodeId);
    if (statusIndex.added.has(id)) return 'added';
    if (statusIndex.removed.has(id)) return 'removed';
    if (statusIndex.moved.has(id)) return 'moved';
    if (statusIndex.modified.has(id)) return 'modified';
    return 'unchanged';
  }
}

// =============================================================================
// React Hook for Projected View
// =============================================================================

import { useMemo } from 'react';

/**
 * React hook for computing projected scenario view with memoization.
 */
export function useProjectedScenarioView(
  baseNodes: Node[],
  baseEdges: Edge[],
  delta: ScenarioDelta | null,
  settings?: Partial<ScenarioProjectionSettings>
): ScenarioProjectionResult | null {
  return useMemo(() => {
    if (!delta) return null;
    return ScenarioProjection.applyDelta(baseNodes, baseEdges, delta, settings);
  }, [baseNodes, baseEdges, delta, settings]);
}

/**
 * Hook to get node status for rendering with minimal re-renders.
 */
export function useNodeScenarioStatus(
  nodeId: string,
  statusIndex: ScenarioStatusIndex | null
): NodeScenarioStatus {
  return useMemo(
    () => ScenarioProjection.getNodeStatus(nodeId, statusIndex),
    [nodeId, statusIndex]
  );
}
